import React, { useMemo } from 'react';
import Input from '../../../components/ui/Input';
import CollapsibleSection from '../../../components/form/CollapsibleSection';
import {
  DAY_OF_WEEK_OPTIONS,
  HOUR_OPTIONS,
  MINUTE_OPTIONS,
  RECURRENCE_DAY_PRESETS,
} from '../constants';
import {
  buildCronFromVisual,
  formatCronDescription,
  formatTimezoneLabel,
  isValidCronExpression,
} from '../utils';

function CronSchedulePreview({ cron, timezone }) {
  if (!cron?.trim()) {
    return null;
  }

  const isValid = isValidCronExpression(cron);

  return (
    <div
      className={`rounded-xl px-4 py-3 text-sm ${
        isValid
          ? 'border border-forge-100 bg-forge-50 text-forge-900'
          : 'border border-red-100 bg-red-50 text-red-700'
      }`}
    >
      {isValid ? (
        <>
          <span className="font-medium">Será enviado:</span>{' '}
          {formatCronDescription(cron)}, {formatTimezoneLabel(timezone)}
        </>
      ) : (
        'Expressão inválida — verifique o formato'
      )}
    </div>
  );
}

function RecurringScheduleFields({
  form,
  setFormFields,
  fieldsDisabled,
  idPrefix = 'schedule',
}) {
  const dayPreset = form.recurrenceDayPreset || '1,2,3,4,5';
  const isAdvancedMode = form.scheduleRecurrenceMode === 'advanced';
  const previewCron = useMemo(() => {
    if (isAdvancedMode) {
      return form.cron;
    }

    return buildCronFromVisual({
      minute: form.recurrenceMinute,
      hour: form.recurrenceHour,
      daysOfWeek: form.recurrenceDays,
    });
  }, [
    form.cron,
    form.recurrenceDays,
    form.recurrenceHour,
    form.recurrenceMinute,
    isAdvancedMode,
  ]);

  const applyVisualRecurrence = (partial) => {
    const next = {
      recurrenceMinute: form.recurrenceMinute,
      recurrenceHour: form.recurrenceHour,
      recurrenceDays: form.recurrenceDays,
      recurrenceDayPreset: form.recurrenceDayPreset,
      ...partial,
    };

    setFormFields({
      scheduleRecurrenceMode: 'visual',
      ...next,
      cron: buildCronFromVisual({
        minute: next.recurrenceMinute,
        hour: next.recurrenceHour,
        daysOfWeek: next.recurrenceDays,
      }),
    });
  };

  const handleHourChange = (event) => {
    applyVisualRecurrence({ recurrenceHour: event.target.value });
  };

  const handleMinuteChange = (event) => {
    applyVisualRecurrence({ recurrenceMinute: event.target.value });
  };

  const handlePresetChange = (presetValue) => {
    if (presetValue === 'custom') {
      setFormFields({
        scheduleRecurrenceMode: 'visual',
        recurrenceDayPreset: 'custom',
      });
      return;
    }

    applyVisualRecurrence({
      recurrenceDayPreset: presetValue,
      recurrenceDays: presetValue,
    });
  };

  const handleDayToggle = (dayValue) => {
    const currentDays = form.recurrenceDays === '*'
      ? []
      : form.recurrenceDays.split(',').filter(Boolean);
    const hasDay = currentDays.includes(dayValue);
    const nextDays = hasDay
      ? currentDays.filter((day) => day !== dayValue)
      : [...currentDays, dayValue];

    applyVisualRecurrence({
      recurrenceDayPreset: 'custom',
      recurrenceDays: nextDays.join(',') || '1',
    });
  };

  const handleAdvancedCronChange = (event) => {
    setFormFields({
      scheduleRecurrenceMode: 'advanced',
      cron: event.target.value,
    });
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-surface-border bg-white p-4 space-y-4">
        <div>
          <p className="text-sm font-semibold text-content-primary">Frequência de envio</p>
          <p className="mt-1 text-xs text-content-muted">
            Escolha um horário e os dias em que a mensagem deve ser enviada.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:max-w-md">
          <div>
            <label
              htmlFor={`${idPrefix}-recurrence-hour`}
              className="block text-sm font-medium text-slate-700 mb-2"
            >
              Hora
            </label>
            <select
              id={`${idPrefix}-recurrence-hour`}
              value={form.recurrenceHour}
              onChange={handleHourChange}
              disabled={fieldsDisabled || isAdvancedMode}
              className="field-input w-full"
              required
            >
              {HOUR_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor={`${idPrefix}-recurrence-minute`}
              className="block text-sm font-medium text-slate-700 mb-2"
            >
              Minuto
            </label>
            <select
              id={`${idPrefix}-recurrence-minute`}
              value={form.recurrenceMinute}
              onChange={handleMinuteChange}
              disabled={fieldsDisabled || isAdvancedMode}
              className="field-input w-full"
              required
            >
              {MINUTE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <p className="block text-sm font-medium text-slate-700 mb-2">Dias da semana</p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {RECURRENCE_DAY_PRESETS.map((preset) => (
              <label
                key={preset.value}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                  dayPreset === preset.value
                    ? 'border-forge-300 bg-forge-50 text-forge-800'
                    : 'border-surface-border bg-white text-content-primary'
                } ${fieldsDisabled || isAdvancedMode ? 'opacity-60' : ''}`}
              >
                <input
                  type="radio"
                  name={`${idPrefix}-recurrence-day-preset`}
                  value={preset.value}
                  checked={dayPreset === preset.value}
                  onChange={() => handlePresetChange(preset.value)}
                  disabled={fieldsDisabled || isAdvancedMode}
                />
                {preset.label}
              </label>
            ))}
          </div>

          {dayPreset === 'custom' && (
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {DAY_OF_WEEK_OPTIONS.map((day) => {
                const selectedDays = form.recurrenceDays === '*'
                  ? DAY_OF_WEEK_OPTIONS.map((option) => option.value)
                  : form.recurrenceDays.split(',').filter(Boolean);
                const checked = selectedDays.includes(day.value);

                return (
                  <label
                    key={day.value}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                      checked
                        ? 'border-forge-200 bg-forge-50 text-forge-800'
                        : 'border-surface-border bg-white text-content-primary'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleDayToggle(day.value)}
                      disabled={fieldsDisabled || isAdvancedMode}
                    />
                    {day.label}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <CronSchedulePreview cron={previewCron} timezone={form.timezone} />
      </div>

      <CollapsibleSection
        title="Expressão cron avançada"
        description="Use apenas se precisar de uma frequência fora dos presets acima."
        summary={isAdvancedMode ? form.cron || 'Expressão personalizada' : 'Opcional'}
        defaultOpen={isAdvancedMode}
      >
        <Input
          id={`${idPrefix}-cron-advanced`}
          label="Expressão cron"
          value={form.cron}
          onChange={handleAdvancedCronChange}
          placeholder="0 9 * * 1-5"
          disabled={fieldsDisabled}
          hint="Ex.: */15 8-18 * * 1-5 para a cada 15 minutos em horário comercial."
        />
        {isAdvancedMode && (
          <CronSchedulePreview cron={form.cron} timezone={form.timezone} />
        )}
      </CollapsibleSection>
    </div>
  );
}

export default RecurringScheduleFields;
